import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { JsonTreeView } from './JsonTreeView';
import { SlideViewer, type Slide } from './SlideViewer';

/* ===================================================================
   STYLED COMPONENTS
   =================================================================== */

const ExplorerRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const ViewModeBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  flex-shrink: 0;
`;

const ViewModeBtn = styled.button<{ $active?: boolean }>`
  font-size: 0.6875rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: ${({ $active }) => ($active ? 'var(--accent-subtle)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--text-muted)')};
  cursor: pointer;
  font-family: inherit;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};

  &:hover {
    background: var(--bg-elevated);
    color: var(--text-secondary);
  }
`;

const FullViewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-secondary);
  cursor: pointer;
  font-family: inherit;
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
  margin-left: auto;
  transition: all 0.15s ease;

  &:hover {
    background: var(--accent-subtle);
    border-color: var(--accent);
    color: var(--accent);
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const ExplorerScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const MarkdownContent = styled.div`
  font-size: 0.875rem;
  line-height: 1.7;
  word-break: break-word;

  h1, h2, h3, h4 { margin: 1em 0 0.5em; color: var(--text-primary); }
  h1 { font-size: 1.2em; }
  h2 { font-size: 1.1em; }
  p { margin: 0.5em 0; }
  ul, ol { margin: 0.5em 0; padding-left: 1.5em; }
  code { font-family: monospace; font-size: 0.9em; padding: 0.1em 0.4em; background: var(--code-bg); border-radius: 4px; }
  pre { margin: 0.75em 0; padding: 1rem; background: var(--code-bg); border-radius: 8px; overflow-x: auto; }
  pre code { padding: 0; background: none; }
  table { border-collapse: collapse; width: 100%; font-size: 0.9em; margin: 0.5em 0; }
  th, td { padding: 0.5em 0.75em; border: 1px solid var(--border); text-align: left; }
  th { background: var(--bg-elevated); font-weight: 600; }
  blockquote { margin: 0.5em 0; padding-left: 1em; border-left: 3px solid var(--accent); color: var(--text-muted); }
  a { color: var(--accent); }
`;

const PlainTextContent = styled.pre`
  font-size: 0.875rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  color: var(--text-secondary);
`;

const JsonTreeWrap = styled.div`
  padding: 0.5rem 0;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  margin: 0.5rem 0;

  th, td {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border);
    text-align: left;
  }

  th {
    background: var(--bg-elevated);
    font-weight: 600;
    color: var(--text-primary);
  }

  tr:hover td {
    background: var(--bg-elevated);
  }
`;

const ExpandRowBtn = styled.button`
  font-size: 0.6875rem;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: var(--accent-subtle);
    color: var(--accent);
  }
`;

const RawJsonPre = styled.pre`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--text-secondary);
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  padding: 1rem;
  background: var(--code-bg);
  border-radius: 8px;
`;

const SegmentDivider = styled.hr`
  border: none;
  border-top: 1px dashed var(--border);
  margin: 1rem 0;
`;

const SegmentLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

/* ===================================================================
   TYPES
   =================================================================== */

export type ViewMode = 'auto' | 'tree' | 'table' | 'raw';

export type SegmentType = 'json' | 'csv' | 'markdown' | 'text' | 'html';

export interface ContentSegment {
  type: SegmentType;
  content: string;
  parsed?: unknown;
  label?: string;
}

export interface RenderDirective {
  type: SegmentType;
  startOffset?: number;
  endOffset?: number;
  content?: string;
  viewHint?: 'tree' | 'table' | 'raw';
  label?: string;
}

interface ResponseExplorerProps {
  content: string;
  directives?: RenderDirective[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  onFullViewClick?: () => void;
}

/* ===================================================================
   HELPERS
   =================================================================== */

function tryParseJson(str: string): unknown {
  const trimmed = str.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }
}

function isArrayOfObjects(arr: unknown[]): boolean {
  return arr.length > 0 && arr.every((v) => v !== null && typeof v === 'object' && !Array.isArray(v));
}

function getTableColumns(data: Record<string, unknown>[]): string[] {
  const keys = new Set<string>();
  data.forEach((row) => Object.keys(row).forEach((k) => keys.add(k)));
  return Array.from(keys);
}

/* ---- CSV helpers ---- */

function detectCsv(text: string): boolean {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return false;

  const delimiter = detectDelimiter(lines[0]);
  if (!delimiter) return false;

  const headerCount = splitCsvLine(lines[0], delimiter).length;
  if (headerCount < 2) return false;

  let consistent = 0;
  for (let i = 1; i < Math.min(lines.length, 6); i++) {
    const cols = splitCsvLine(lines[i], delimiter).length;
    if (cols === headerCount) consistent++;
  }
  return consistent >= Math.min(lines.length - 1, 3);
}

function detectDelimiter(line: string): string | null {
  const commas = (line.match(/,/g) || []).length;
  const tabs = (line.match(/\t/g) || []).length;
  if (commas >= 1) return ',';
  if (tabs >= 1) return '\t';
  return null;
}

function splitCsvLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === delimiter && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.trim().split('\n').filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };
  const delimiter = detectDelimiter(lines[0]) || ',';
  const headers = splitCsvLine(lines[0], delimiter);
  const rows = lines.slice(1).map((line) => splitCsvLine(line, delimiter));
  return { headers, rows };
}

/* ---- Content segmenter ---- */

function segmentContent(content: string, directives?: RenderDirective[]): ContentSegment[] {
  if (directives && directives.length > 0) {
    return directives.map((d) => ({
      type: d.type,
      content: d.content ?? content.slice(d.startOffset ?? 0, d.endOffset ?? content.length),
      parsed: d.type === 'json' ? tryParseJson(d.content ?? '') : undefined,
      label: d.label,
    }));
  }

  const trimmed = content.trim();

  const pureJson = tryParseJson(trimmed);
  if (pureJson !== null) {
    if (hasSlidesPayload(pureJson)) {
      return [{ type: 'html', content: trimmed, parsed: pureJson, label: 'Presentation Slides' }];
    }
    return [{ type: 'json', content: trimmed, parsed: pureJson }];
  }

  if (detectCsv(trimmed) && !hasMarkdownIndicators(trimmed)) {
    return [{ type: 'csv', content: trimmed }];
  }

  const segments: ContentSegment[] = [];
  const fencedBlockRegex = /```(json|csv)?\s*\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = fencedBlockRegex.exec(trimmed)) !== null) {
    if (match.index > lastIndex) {
      const before = trimmed.slice(lastIndex, match.index).trim();
      if (before) segments.push(...segmentPlainOrMixed(before));
    }

    const lang = match[1]?.toLowerCase();
    const blockContent = match[2].trim();

    if (lang === 'csv' || (lang !== 'json' && detectCsv(blockContent))) {
      segments.push({ type: 'csv', content: blockContent, label: 'CSV Data' });
    } else {
      const parsed = tryParseJson(blockContent);
      if (parsed !== null) {
        segments.push({ type: 'json', content: blockContent, parsed, label: 'JSON' });
      } else {
        segments.push({ type: 'text', content: match[0] });
      }
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex === 0) {
    return segmentPlainOrMixed(trimmed);
  }

  if (lastIndex < trimmed.length) {
    const after = trimmed.slice(lastIndex).trim();
    if (after) segments.push(...segmentPlainOrMixed(after));
  }

  return segments.length > 0 ? segments : [{ type: 'text', content: trimmed }];
}

function segmentPlainOrMixed(text: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  const jsonBlockRegex = /(?:^|\n)([\t ]*[{\[][\s\S]*?[\t ]*[}\]])(?:\n|$)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = jsonBlockRegex.exec(text)) !== null) {
    const candidate = match[1].trim();
    const parsed = tryParseJson(candidate);
    if (parsed === null) continue;

    if (match.index > lastIndex) {
      const before = text.slice(lastIndex, match.index).trim();
      if (before) segments.push(classifyTextSegment(before));
    }
    segments.push({ type: 'json', content: candidate, parsed });
    lastIndex = match.index + match[0].length;
  }

  if (segments.length === 0) {
    return [classifyTextSegment(text)];
  }

  if (lastIndex < text.length) {
    const after = text.slice(lastIndex).trim();
    if (after) segments.push(classifyTextSegment(after));
  }

  return segments;
}

function hasMarkdownIndicators(text: string): boolean {
  return /[#*_`\[\]|>~-]{2}|^\s*[-*+]\s|^\s*\d+\.\s|^#{1,6}\s|```/m.test(text);
}

function hasSlidesPayload(parsed: unknown): parsed is { slides: Slide[] } {
  if (typeof parsed !== 'object' || parsed === null) return false;
  const obj = parsed as Record<string, unknown>;
  return (
    Array.isArray(obj.slides) &&
    obj.slides.length > 0 &&
    obj.slides.every(
      (s: unknown) =>
        typeof s === 'object' && s !== null &&
        'slide_number' in s && 'title' in s && 'html' in s,
    )
  );
}

function classifyTextSegment(text: string): ContentSegment {
  if (detectCsv(text)) return { type: 'csv', content: text };
  if (hasMarkdownIndicators(text)) return { type: 'markdown', content: text };
  return { type: 'text', content: text };
}

/* ===================================================================
   SUB-COMPONENTS
   =================================================================== */

const CsvTableView: React.FC<{ content: string }> = ({ content }) => {
  const { headers, rows } = useMemo(() => parseCsv(content), [content]);

  if (headers.length === 0) return <PlainTextContent>{content}</PlainTextContent>;

  return (
    <DataTable>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {headers.map((_, ci) => (
              <td key={ci}>{row[ci] ?? ''}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </DataTable>
  );
};

const JsonSegmentView: React.FC<{
  content: string;
  parsed: unknown;
  viewMode: ViewMode;
}> = ({ content, parsed, viewMode }) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const effectiveView = useMemo(() => {
    if (viewMode !== 'auto') return viewMode;
    if (Array.isArray(parsed) && isArrayOfObjects(parsed as Record<string, unknown>[])) return 'table';
    if (typeof parsed === 'object' && parsed !== null) return 'tree';
    return 'raw';
  }, [viewMode, parsed]);

  const toggleRow = (i: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  if (effectiveView === 'raw') {
    return <RawJsonPre>{typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : content}</RawJsonPre>;
  }

  if (effectiveView === 'tree' && parsed !== null && typeof parsed === 'object') {
    return (
      <JsonTreeWrap>
        <JsonTreeView data={parsed as Parameters<typeof JsonTreeView>[0]['data']} />
      </JsonTreeWrap>
    );
  }

  if (
    effectiveView === 'table' &&
    Array.isArray(parsed) &&
    isArrayOfObjects(parsed as Record<string, unknown>[])
  ) {
    const rows = parsed as Record<string, unknown>[];
    const columns = getTableColumns(rows);

    return (
      <DataTable>
        <thead>
          <tr>
            <th style={{ width: 40 }}></th>
            {columns.map((col) => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <React.Fragment key={i}>
              <tr>
                <td>
                  {columns.some((c) => {
                    const v = row[c];
                    return v !== null && typeof v === 'object';
                  }) && (
                    <ExpandRowBtn onClick={() => toggleRow(i)}>
                      {expandedRows.has(i) ? '▾' : '▸'}
                    </ExpandRowBtn>
                  )}
                </td>
                {columns.map((col) => {
                  const v = row[col];
                  const isComplex = v !== null && typeof v === 'object';
                  return (
                    <td key={col}>
                      {isComplex
                        ? expandedRows.has(i)
                          ? JSON.stringify(v)
                          : `{…}`
                        : String(v ?? '')}
                    </td>
                  );
                })}
              </tr>
              {expandedRows.has(i) &&
                columns.some((c) => {
                  const v = row[c];
                  return v !== null && typeof v === 'object';
                }) && (
                  <tr>
                    <td colSpan={columns.length + 1} style={{ padding: '0.5rem 0.75rem', background: 'var(--code-bg)' }}>
                      <JsonTreeView data={row as Parameters<typeof JsonTreeView>[0]['data']} />
                    </td>
                  </tr>
                )}
            </React.Fragment>
          ))}
        </tbody>
      </DataTable>
    );
  }

  return <RawJsonPre>{content}</RawJsonPre>;
};

/* ===================================================================
   MAIN COMPONENT
   =================================================================== */

const ExpandIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="10 2 14 2 14 6" />
    <polyline points="6 14 2 14 2 10" />
    <line x1="14" y1="2" x2="9.5" y2="6.5" />
    <line x1="2" y1="14" x2="6.5" y2="9.5" />
  </svg>
);

export const ResponseExplorer: React.FC<ResponseExplorerProps> = ({
  content,
  directives,
  viewMode: controlledMode,
  onViewModeChange,
  onFullViewClick,
}) => {
  const [internalMode, setInternalMode] = useState<ViewMode>('auto');
  const viewMode = controlledMode ?? internalMode;
  const setViewMode = onViewModeChange ?? setInternalMode;

  const segments = useMemo(() => segmentContent(content, directives), [content, directives]);

  const hasJson = segments.some((s) => s.type === 'json');
  const hasCsv = segments.some((s) => s.type === 'csv');
  const showModeBar = hasJson || !!onFullViewClick;
  const isMultiSegment = segments.length > 1;

  return (
    <ExplorerRoot>
      {showModeBar && (
        <ViewModeBar>
          {hasJson && (
            <>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginRight: '0.5rem' }}>
                View:
              </span>
              <ViewModeBtn $active={viewMode === 'auto'} onClick={() => setViewMode('auto')}>
                Auto
              </ViewModeBtn>
              <ViewModeBtn $active={viewMode === 'tree'} onClick={() => setViewMode('tree')}>
                Tree
              </ViewModeBtn>
              <ViewModeBtn $active={viewMode === 'table'} onClick={() => setViewMode('table')}>
                Table
              </ViewModeBtn>
              <ViewModeBtn $active={viewMode === 'raw'} onClick={() => setViewMode('raw')}>
                Raw
              </ViewModeBtn>
            </>
          )}
          {onFullViewClick && (
            <FullViewBtn onClick={onFullViewClick} title="Expand to full view">
              <ExpandIcon />Full View
            </FullViewBtn>
          )}
        </ViewModeBar>
      )}
      <ExplorerScroll>
        {segments.map((segment, idx) => (
          <React.Fragment key={idx}>
            {isMultiSegment && idx > 0 && <SegmentDivider />}
            {isMultiSegment && segment.label && <SegmentLabel>{segment.label}</SegmentLabel>}
            <SegmentRenderer segment={segment} viewMode={viewMode} hasCsv={hasCsv} />
          </React.Fragment>
        ))}
      </ExplorerScroll>
    </ExplorerRoot>
  );
};

const SegmentRenderer: React.FC<{
  segment: ContentSegment;
  viewMode: ViewMode;
  hasCsv: boolean;
}> = ({ segment, viewMode }) => {
  switch (segment.type) {
    case 'json':
      return (
        <JsonSegmentView
          content={segment.content}
          parsed={segment.parsed ?? tryParseJson(segment.content)}
          viewMode={viewMode}
        />
      );
    case 'csv':
      return <CsvTableView content={segment.content} />;
    case 'markdown':
      return (
        <MarkdownContent>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{segment.content}</ReactMarkdown>
        </MarkdownContent>
      );
    case 'html': {
      const parsed = segment.parsed as { slides?: Slide[] } | undefined;
      const slides = parsed?.slides;
      if (slides && slides.length > 0) {
        return <SlideViewer slides={slides} />;
      }
      return <PlainTextContent>{segment.content}</PlainTextContent>;
    }
    case 'text':
    default:
      return <PlainTextContent>{segment.content}</PlainTextContent>;
  }
};

export default ResponseExplorer;
